import React from "react";

export type CommercialInvoicePreviewDto = {
  facture: {
    id: number;
    numero: string;
    statut: string;
    dateEmission: string;
    dateEcheance: string | null;
    devise: string;
    montantHT: number;
    montantTVA: number;
    montantTTC: number;
    soldeDu: number;
  };
  client: {
    nom: string | null;
    adresse: string | null;
    telephone: string | null;
    email: string | null;
    reference: string | null;
  };
  company: {
    nomLegal: string | null;
    slogan: string | null;
    adresse: string | null;
    telephone: string | null;
    email: string | null;
    siteWeb: string | null;
    rccm: string | null;
    nif: string | null;
    idNat: string | null;
    impot: string | null;
    bank: string | null;
    iban: string | null;
    swift: string | null;
    mobileMoney: string | null;
    conditionsPaiement?: string | null;
    penaliteRetard?: string | null;
    piedPageFacture?: string | null;
    signatureName?: string | null;
    signatureRole?: string | null;
    signatureUrl?: string | null;
  };
  lignes: Array<{
    description: string;
    quantite: number;
    prixUnitaireHT: number;
    montantHT: number;
    devise: string;
  }>;
  payments: Array<{
    id: number;
    montant: number;
    datePaiement: string;
    modePaiement: string;
    reference: string | null;
  }>;
  pdf: {
    status: string;
    url: string | null;
    lastGeneratedAt: string | null;
    lastError: string | null;
  };
};

function value(v: string | null | undefined): string {
  return v?.trim() ? v.trim() : "Non renseigné";
}

function money(amount: number, devise: string): string {
  return `${amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${devise}`;
}

function date(valueIso: string | null): string {
  if (!valueIso) return "Non renseigné";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(new Date(valueIso));
}

export function CommercialInvoicePreview({ data }: { data: CommercialInvoicePreviewDto }): React.JSX.Element {
  const bankLines = [
    data.company.bank ? `Banque / compte : ${data.company.bank}` : null,
    data.company.iban ? `IBAN : ${data.company.iban}` : null,
    data.company.swift ? `SWIFT : ${data.company.swift}` : null,
    data.company.mobileMoney ? `Mobile money : ${data.company.mobileMoney}` : null,
  ].filter((line): line is string => Boolean(line));

  return (
    <article className="mx-auto min-h-[1123px] w-full max-w-[794px] bg-white px-12 py-10 text-slate-900 shadow-2xl print:shadow-none">
      <header className="flex justify-between gap-8 border-b border-slate-200 pb-6">
        <div className="max-w-[58%]">
          <h1 className="text-xl font-bold uppercase tracking-wide">{value(data.company.nomLegal)}</h1>
          {data.company.slogan ? <p className="mt-1 text-sm text-slate-600">{data.company.slogan}</p> : null}
          <div className="mt-4 space-y-0.5 text-xs leading-relaxed text-slate-600">
            <p className="whitespace-pre-line">{value(data.company.adresse)}</p>
            <p>Tél. {value(data.company.telephone)} · {value(data.company.email)}</p>
            <p>{value(data.company.siteWeb)}</p>
            <p>RCCM : {value(data.company.rccm)} · NIF : {value(data.company.nif)} · ID NAT : {value(data.company.idNat)}</p>
            <p>N° impôt : {value(data.company.impot)}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-black tracking-tight">FACTURE</p>
          <p className="mt-2 font-mono text-sm">{data.facture.numero}</p>
          <dl className="mt-5 space-y-1 text-xs">
            <div><dt className="inline text-slate-500">Émission : </dt><dd className="inline">{date(data.facture.dateEmission)}</dd></div>
            <div><dt className="inline text-slate-500">Échéance : </dt><dd className="inline">{date(data.facture.dateEcheance)}</dd></div>
            <div><dt className="inline text-slate-500">Statut : </dt><dd className="inline font-semibold">{data.facture.statut}</dd></div>
          </dl>
        </div>
      </header>

      <section className="mt-8 grid grid-cols-2 gap-8 text-sm">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Facturé à</p>
          <p className="mt-2 font-semibold">{value(data.client.nom)}</p>
          <p className="mt-1 whitespace-pre-line text-slate-600">{value(data.client.adresse)}</p>
          <p className="mt-1 text-slate-600">{value(data.client.telephone)}</p>
          <p className="text-slate-600">{value(data.client.email)}</p>
          <p className="mt-1 text-xs text-slate-500">Référence : {value(data.client.reference)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Document facture</p>
          <p className="mt-2 text-sm">
            <span className="font-semibold">
              {data.pdf.url ? "Document PDF disponible" : "Aperçu commercial disponible"}
            </span>
          </p>
        </div>
      </section>

      <table className="mt-8 w-full border-collapse text-sm">
        <thead>
          <tr className="border-y border-slate-300 bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
            <th className="py-3 pr-3">Description</th>
            <th className="py-3 pr-3 text-right">Qté</th>
            <th className="py-3 pr-3 text-right">P.U. HT</th>
            <th className="py-3 text-right">Montant HT</th>
          </tr>
        </thead>
        <tbody>
          {data.lignes.length === 0 ? (
            <tr><td colSpan={4} className="py-8 text-center text-slate-500">Aucune ligne facture enregistrée.</td></tr>
          ) : (
            data.lignes.map((ligne, index) => (
              <tr key={`${ligne.description}-${index}`} className="border-b border-slate-100">
                <td className="py-3 pr-3">{ligne.description || "Non renseigné"}</td>
                <td className="py-3 pr-3 text-right tabular-nums">{ligne.quantite.toLocaleString("fr-FR")}</td>
                <td className="py-3 pr-3 text-right tabular-nums">{money(ligne.prixUnitaireHT, ligne.devise)}</td>
                <td className="py-3 text-right font-medium tabular-nums">{money(ligne.montantHT, ligne.devise)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <section className="mt-6 ml-auto w-full max-w-xs space-y-2 text-sm">
        <Line label="Total HT" value={money(data.facture.montantHT, data.facture.devise)} />
        <Line label="TVA" value={money(data.facture.montantTVA, data.facture.devise)} />
        <Line label="Total TTC" value={money(data.facture.montantTTC, data.facture.devise)} strong />
        <Line label="Solde dû" value={money(data.facture.soldeDu, data.facture.devise)} strong />
      </section>

      <section className="mt-10 grid grid-cols-2 gap-8 border-t border-slate-200 pt-6 text-xs text-slate-700">
        <div>
          <p className="font-bold text-slate-900">Coordonnées bancaires</p>
          {bankLines.length > 0 ? <ul className="mt-2 list-inside list-disc space-y-1">{bankLines.map((line) => <li key={line}>{line}</li>)}</ul> : <p className="mt-2">Non renseigné</p>}
        </div>
        <div>
          <p className="font-bold text-slate-900">Conditions de paiement</p>
          <p className="mt-2 whitespace-pre-line">{value(data.company.conditionsPaiement)}</p>
          {data.company.penaliteRetard ? <p className="mt-2 whitespace-pre-line text-slate-500">{data.company.penaliteRetard}</p> : null}
        </div>
      </section>

      {data.payments.length > 0 ? (
        <section className="mt-8 text-xs">
          <p className="font-bold text-slate-900">Paiements enregistrés</p>
          <ul className="mt-2 space-y-1 text-slate-600">
            {data.payments.map((payment) => (
              <li key={payment.id}>{date(payment.datePaiement)} · {money(payment.montant, data.facture.devise)} · {payment.modePaiement}{payment.reference ? ` · ${payment.reference}` : ""}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <footer className="mt-12 flex items-end justify-between gap-8 text-xs text-slate-500">
        <p className="max-w-md whitespace-pre-line">{value(data.company.piedPageFacture)}</p>
        <div className="min-w-[180px] text-center">
          {data.company.signatureUrl ? <img src={data.company.signatureUrl} alt="" className="mx-auto h-16 object-contain" /> : <div className="mx-auto h-16 rounded border border-dashed border-slate-300" />}
          <p className="mt-2 font-semibold text-slate-800">{value(data.company.signatureName)}</p>
          <p>{value(data.company.signatureRole)}</p>
        </div>
      </footer>
    </article>
  );
}

function Line({ label, value, strong = false }: { label: string; value: string; strong?: boolean }): React.JSX.Element {
  return (
    <div className={`flex justify-between border-b border-slate-200 py-1 ${strong ? "font-bold text-slate-950" : "text-slate-700"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
